import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg, itDate, download } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Printer, Trash, Wrench, MagnifyingGlass, ArrowsLeftRight } from "@phosphor-icons/react";

const oggi = () => new Date().toISOString().slice(0, 10);

export default function ToolDeliveries() {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tools, setTools] = useState([]);
  const [f, setF] = useState({ employee_id: "all", department: "all", date_from: "", date_to: "" });
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [mese, setMese] = useState(oggi().slice(0, 7));

  const load = useCallback(async () => {
    try {
      setRows((await api.get("/safety/tool-deliveries", { params: f })).data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/safety/tools", { params: { only_active: true } }).then((r) => setTools(r.data)).catch(() => {});
  }, []);

  const visibili = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => `${r.employee_name || ""} ${r.employee_code || ""} ${r.description || ""}`
      .toLowerCase().includes(t));
  }, [rows, q]);
  const totale = useMemo(() => visibili.reduce((s, r) => s + (Number(r.quantity) || 1), 0), [visibili]);

  const salva = async () => {
    try {
      const items = (form.items || [])
        .filter((i) => i.tool_id || (i.description || "").trim())
        .map((i) => ({ tool_id: i.tool_id || null, description: i.description || "",
                       quantity: Number(i.quantity || 1), size: i.size || "" }));
      const { data } = await api.post("/safety/tool-deliveries", {
        employee_id: form.employee_id, date: form.date, notes: form.notes, items });
      toast.success(`Consegna n. ${data.progressive} registrata · ${data.righe} attrezzature`);
      setForm(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const stampa = (r) => download(`/safety/tool-deliveries/${r.id}/pdf`,
    `consegna_attrezzature_${r.progressive || r.id}.pdf`).catch((e) => toast.error(errMsg(e)));

  const migra = async () => {
    try {
      const { data } = await api.post("/safety/tool-deliveries/migrate", null, { params: { dry_run: true } });
      if (!data.consegne) { toast.success("Nessuna consegna di sole attrezzature da spostare"); return; }
      if (!window.confirm(`Spostare ${data.consegne} consegne (${data.righe} attrezzature) dal Registro DPI a questo registro?`
        + (data.miste_rimaste ? `\n${data.miste_rimaste} consegne miste DPI+attrezzature restano nel Registro DPI.` : ""))) return;
      const res = await api.post("/safety/tool-deliveries/migrate");
      toast.success(`${res.data.consegne} consegne spostate · ${res.data.righe} attrezzature`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const elimina = async (r) => {
    if (!window.confirm(`Eliminare la consegna di "${r.description}" a ${r.employee_name}?`)) return;
    try { await api.delete(`/safety/tool-deliveries/${r.id}`); toast.success("Consegna eliminata"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const pdf = async () => {
    const [y, m] = mese.split("-");
    try {
      const res = await api.get("/safety/tool-deliveries/monthly-pdf",
        { params: { year: Number(y), month: Number(m) }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-3" data-testid="tool-deliveries-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Registro Attrezzature di Lavoro</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Attrezzature consegnate ai dipendenti · nessun modulo di firma, riepilogo mensile in PDF per il Titolare
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            {user?.role === "admin" && (
              <Button size="sm" variant="outline" className="rounded-sm font-semibold"
                data-testid="tool-migrate" onClick={migra}>
                <ArrowsLeftRight size={15} className="mr-1.5" /> Sposta attrezzature dal registro DPI
              </Button>
            )}
            <Button size="sm" className="rounded-sm font-semibold" data-testid="tool-delivery-add"
              onClick={() => setForm({ employee_id: "", date: oggi(), notes: "",
                items: [{ uid: Math.random().toString(36).slice(2, 8), tool_id: "", description: "", quantity: 1, size: "" }] })}>
              <Plus size={15} className="mr-1.5" /> Nuova consegna
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-sm border border-border bg-card p-3 grid md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Cerca</Label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome dipendente..."
              className="h-9 rounded-sm pl-8" data-testid="tool-search" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dipendente</Label>
          <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
            <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="tool-filter-employee"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tutti i dipendenti</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reparto</Label>
          <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
            <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="tool-filter-department"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tutti i reparti</SelectItem>
              {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dal</Label>
          <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })}
            className="h-9 rounded-sm mt-1" data-testid="tool-filter-from" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Al</Label>
          <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })}
            className="h-9 rounded-sm mt-1" data-testid="tool-filter-to" />
        </div>
        <div className="flex gap-2">
          <Input type="month" value={mese} onChange={(e) => setMese(e.target.value)}
            className="h-9 rounded-sm" data-testid="tool-pdf-month" />
          <Button variant="outline" size="sm" className="h-9 rounded-sm font-semibold whitespace-nowrap"
            data-testid="tool-monthly-pdf" onClick={pdf}>
            <Printer size={15} className="mr-1.5" /> PDF mese
          </Button>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm" data-testid="tool-deliveries-table">
          <thead className="bg-secondary"><tr>
            {["", "N.", "DATA", "DIPENDENTE", "REPARTO", "ATTREZZATURA", "Q.TÀ", "CONSEGNATA DA"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {visibili.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`tool-delivery-row-${r.id}`}>
                <td className="px-3 py-1.5 text-left whitespace-nowrap">
                  <div className="inline-flex items-center gap-2">
                    <button type="button" title="Stampa modulo di consegna"
                      data-testid={`tool-delivery-print-${r.id}`}
                      className="rounded-sm p-1.5 text-muted-foreground hover:text-primary"
                      onClick={() => stampa(r)}>
                      <Printer size={16} />
                    </button>
                    {canEdit && (
                      <button type="button" title="Elimina consegna" data-testid={`tool-delivery-del-${r.id}`}
                        className="rounded-sm p-1.5 text-rose-600 hover:text-rose-700" onClick={() => elimina(r)}>
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 tabular-nums font-semibold">{r.progressive || "—"}</td>
                <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{itDate(r.date)}</td>
                <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{r.employee_name}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.department || "—"}</td>
                <td className="px-3 py-1.5">{r.tool_code ? `${r.tool_code} — ` : ""}{r.description}</td>
                <td className="px-3 py-1.5 tabular-nums">{r.quantity || 1}</td>
                <td className="px-3 py-1.5 text-muted-foreground text-xs">{r.delivered_by}</td>
              </tr>
            ))}
            {visibili.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground" data-testid="tool-deliveries-empty">
                {q.trim() ? `Nessuna consegna per "${q}"` : "Nessuna consegna di attrezzature"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="tool-deliveries-count">
        {visibili.length} consegne · {totale} attrezzature
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="tool-delivery-dialog">
            <DialogHeader>
              <DialogTitle className="font-head">Consegna attrezzature di lavoro</DialogTitle>
              <DialogDescription className="text-xs">
                Puoi aggiungere più righe nella stessa consegna, come nel Registro DPI
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="tool-delivery-employee">
                  <SelectValue placeholder="Dipendente" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
                </SelectContent>
              </Select>

              <div className="flex items-center justify-between pt-1">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Attrezzature consegnate
                </div>
                <Button size="sm" variant="outline" className="rounded-sm" data-testid="tool-add-item"
                  onClick={() => setForm({ ...form, items: [...form.items,
                    { uid: Math.random().toString(36).slice(2, 8), tool_id: "", description: "", quantity: 1, size: "" }] })}>
                  <Plus size={14} className="mr-1" /> Aggiungi riga
                </Button>
              </div>

              {form.items.map((it, idx) => (
                <div key={it.uid} className="grid sm:grid-cols-12 gap-2 items-end border border-border rounded-sm p-2"
                  data-testid={`tool-item-${idx}`}>
                  <div className="sm:col-span-4">
                    <Label className="text-[10px] uppercase text-muted-foreground">Attrezzatura in elenco</Label>
                    <Select value={it.tool_id} onValueChange={(v) => {
                      const items = [...form.items];
                      const t = tools.find((x) => x.id === v);
                      items[idx] = { ...it, tool_id: v, description: it.description || t?.name || "" };
                      setForm({ ...form, items });
                    }}>
                      <SelectTrigger className="h-9 rounded-sm mt-1" data-testid={`tool-item-tool-${idx}`}>
                        <SelectValue placeholder="Seleziona (opzionale)" />
                      </SelectTrigger>
                      <SelectContent className="max-h-56">
                        {tools.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} — {t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-3">
                    <Label className="text-[10px] uppercase text-muted-foreground">Descrizione</Label>
                    <Input value={it.description} placeholder="se non in elenco"
                      className="h-9 rounded-sm mt-1" data-testid={`tool-item-desc-${idx}`}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, description: e.target.value };
                        setForm({ ...form, items });
                      }} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[11px] font-semibold uppercase text-muted-foreground">Q.tà</Label>
                    <Input type="number" min="1" inputMode="numeric" value={it.quantity}
                      className="h-11 min-w-[72px] rounded-sm mt-1 text-base text-center tabular-nums font-semibold"
                      data-testid={`tool-item-qty-${idx}`}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, quantity: e.target.value };
                        setForm({ ...form, items });
                      }} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-[11px] font-semibold uppercase text-muted-foreground">Taglia</Label>
                    <Input value={it.size} placeholder="es. M / 42"
                      className="h-11 min-w-[88px] rounded-sm mt-1 text-base text-center font-semibold"
                      data-testid={`tool-item-size-${idx}`}
                      onChange={(e) => {
                        const items = [...form.items];
                        items[idx] = { ...it, size: e.target.value };
                        setForm({ ...form, items });
                      }} />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    <button type="button" data-testid={`tool-item-remove-${idx}`}
                      className="h-9 px-2 text-muted-foreground hover:text-rose-500"
                      disabled={form.items.length === 1}
                      onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}>
                      <Trash size={15} />
                    </button>
                  </div>
                </div>
              ))}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Data consegna</Label>
                  <Input type="date" className="h-9 rounded-sm mt-1" data-testid="tool-delivery-date"
                    value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase text-muted-foreground">Note</Label>
                  <Input placeholder="facoltative" className="h-9 rounded-sm mt-1" data-testid="tool-delivery-notes"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setForm(null)}>Annulla</Button>
                <Button size="sm" className="rounded-sm font-semibold" data-testid="tool-delivery-save"
                  disabled={!form.employee_id || !form.items.some((i) => i.tool_id || (i.description || "").trim())}
                  onClick={salva}>
                  <Wrench size={14} className="mr-1.5" /> Registra consegna
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
